import {
	CodeBlock,
	Endpoint,
	Alert,
	Badge,
	Table,
	ParamList,
	Param,
	Step,
} from '../../components/docs';
import styles from '../../components/docs/docs.module.css';

export default function GamesCrash() {
	return (
		<div className={styles.docsPage}>
			<h1>Crash Game</h1>
			<p className={styles.lead}>
				A thrilling multiplayer game where a multiplier grows from 1.00x until
				it randomly crashes. Players must cash out before the crash to secure
				their winnings. Features real-time WebSocket updates and social
				gameplay.
			</p>

			<Alert variant="info" title="House Edge">
				The Crash game has a fixed 1% house edge. The crash point is determined
				by a provably fair algorithm that ensures an average 99% return to
				players over time.
			</Alert>

			<h2>Game Overview</h2>
			<p>
				Crash is a social multiplayer game where all players bet on the same
				round. A multiplier starts at 1.00x and increases over time. At a random
				point determined by the provably fair algorithm, the round "crashes" and
				ends. Players must cash out before the crash to win their bet multiplied
				by their cashout multiplier.
			</p>

			<h3>Key Features</h3>
			<ul>
				<li>
					<strong>Multiplayer:</strong> All players participate in the same
					round simultaneously
				</li>
				<li>
					<strong>Real-time:</strong> WebSocket connection for live multiplier
					updates
				</li>
				<li>
					<strong>Auto-cashout:</strong> Set a target multiplier to
					automatically cash out
				</li>
				<li>
					<strong>Social features:</strong> See other players' bets and cashouts
					in real-time
				</li>
				<li>
					<strong>No maximum:</strong> Multiplier can theoretically reach any
					value (record crashes over 10,000x)
				</li>
			</ul>

			<h2>Round Lifecycle</h2>
			<p>Each Crash round goes through distinct phases:</p>

			<Table
				columns={[
					{ key: 'phase', header: 'Phase' },
					{ key: 'duration', header: 'Duration' },
					{ key: 'description', header: 'Description' },
				]}
				data={[
					{
						phase: 'Betting',
						duration: '5-10 seconds',
						description:
							'Players can place bets and set auto-cashout targets. Multiplier shows 1.00x.',
					},
					{
						phase: 'Running',
						duration: 'Variable',
						description:
							'Multiplier increases. Players can manually cash out anytime. Auto-cashouts trigger at target.',
					},
					{
						phase: 'Crashed',
						duration: 'Instant',
						description:
							'Round ends at crash point. Players who did not cash out lose their bets.',
					},
					{
						phase: 'Cooldown',
						duration: '2-3 seconds',
						description:
							'Results displayed. Preparing for next round. No bets accepted.',
					},
				]}
			/>

			<h3>Multiplier Growth</h3>
			<p>
				The multiplier grows exponentially according to a curve that accelerates
				over time:
			</p>

			<CodeBlock language="typescript" title="Multiplier Growth Formula">
				{`// Multiplier increases exponentially over time
// Time is measured in milliseconds from round start

function getMultiplier(elapsedMs: number): number {
  // Growth rate: 6% per second, compounded
  const growthRate = 0.00006; // per millisecond
  const multiplier = Math.pow(Math.E, growthRate * elapsedMs);
  return Math.floor(multiplier * 100) / 100; // Round down to 2 decimals
}

// Examples:
// 0ms    -> 1.00x
// 1000ms -> 1.06x
// 5000ms -> 1.35x
// 10000ms -> 1.82x
// 30000ms -> 6.05x
// 60000ms -> 36.60x`}
			</CodeBlock>

			<h2>Bet Parameters</h2>
			<ParamList>
				<Param name="amount" type="number" required>
					The bet amount in the player's currency. Must be positive and within
					the operator's configured min/max limits.
				</Param>
				<Param name="autoCashout" type="number">
					Optional target multiplier for automatic cashout. When the game
					reaches this multiplier, your bet is automatically cashed out. Must be
					at least 1.01x.
				</Param>
				<Param name="currency" type="string">
					Optional currency code (e.g., "USD", "BTC"). Defaults to the session's
					primary currency.
				</Param>
			</ParamList>

			<Alert variant="warning" title="Timing Matters">
				Bets can only be placed during the betting phase. Cashouts must occur
				while the round is running. Network latency can affect your ability to
				cash out at exact multipliers - auto-cashout is recommended for precise
				targets.
			</Alert>

			<h2>API Endpoints</h2>

			<h3>Get Current Round Status</h3>
			<Endpoint
				method="GET"
				path="/api/v1/games/crash/status"
				description="Get the current round state and active bets"
			/>

			<CodeBlock language="json" title="Response - Betting Phase">
				{`{
  "success": true,
  "data": {
    "roundId": "crash_round_abc123",
    "phase": "betting",
    "multiplier": 1.00,
    "bettingEndsAt": "2024-01-15T18:30:15.000Z",
    "serverSeedHash": "d4e5f6a7b8c9...",
    "activeBets": [
      { "playerId": "p1", "amount": 10.00, "autoCashout": 2.00 },
      { "playerId": "p2", "amount": 25.00, "autoCashout": null }
    ],
    "previousRounds": [
      { "roundId": "crash_round_xyz789", "crashPoint": 1.43 },
      { "roundId": "crash_round_xyz788", "crashPoint": 3.21 },
      { "roundId": "crash_round_xyz787", "crashPoint": 1.02 }
    ]
  }
}`}
			</CodeBlock>

			<CodeBlock language="json" title="Response - Running Phase">
				{`{
  "success": true,
  "data": {
    "roundId": "crash_round_abc123",
    "phase": "running",
    "multiplier": 2.34,
    "startedAt": "2024-01-15T18:30:15.000Z",
    "elapsedMs": 14250,
    "activeBets": [
      { "playerId": "p1", "amount": 10.00, "cashedOut": true, "cashoutMultiplier": 2.00, "payout": 20.00 },
      { "playerId": "p2", "amount": 25.00, "cashedOut": false }
    ]
  }
}`}
			</CodeBlock>

			<h3>Place Bet</h3>
			<Endpoint
				method="POST"
				path="/api/v1/games/crash/bet"
				description="Place a bet for the current or next round"
			/>

			<CodeBlock language="json" title="Request">
				{`{
  "sessionToken": "eyJhbGciOiJIUzI1NiIs...",
  "amount": 15.00,
  "autoCashout": 2.50,
  "currency": "USD"
}`}
			</CodeBlock>

			<CodeBlock language="json" title="Response">
				{`{
  "success": true,
  "data": {
    "betId": "bet_crash_stu456vwx",
    "roundId": "crash_round_abc123",
    "amount": 15.00,
    "autoCashout": 2.50,
    "currency": "USD",
    "status": "active",
    "placedAt": "2024-01-15T18:30:10.123Z"
  }
}`}
			</CodeBlock>

			<h3>Manual Cashout</h3>
			<Endpoint
				method="POST"
				path="/api/v1/games/crash/cashout"
				description="Manually cash out your active bet at the current multiplier"
			/>

			<CodeBlock language="json" title="Request">
				{`{
  "sessionToken": "eyJhbGciOiJIUzI1NiIs...",
  "betId": "bet_crash_stu456vwx"
}`}
			</CodeBlock>

			<CodeBlock language="json" title="Response">
				{`{
  "success": true,
  "data": {
    "betId": "bet_crash_stu456vwx",
    "cashoutMultiplier": 2.87,
    "payout": 43.05,
    "profit": 28.05,
    "balanceAfter": 143.05,
    "cashedOutAt": "2024-01-15T18:30:32.456Z"
  }
}`}
			</CodeBlock>

			<h3>Get Game Info</h3>
			<Endpoint
				method="GET"
				path="/api/v1/games/crash/info"
				description="Get game configuration and limits"
			/>

			<CodeBlock language="json" title="Response">
				{`{
  "success": true,
  "data": {
    "game": "crash",
    "name": "Crash",
    "description": "Multiplayer crash game with growing multiplier",
    "houseEdge": 1.0,
    "minBet": 0.10,
    "maxBet": 1000.00,
    "minAutoCashout": 1.01,
    "bettingDuration": 7000,
    "cooldownDuration": 3000,
    "websocketUrl": "wss://api.ringfort.studio/v1/games/crash/ws"
  }
}`}
			</CodeBlock>

			<h2>WebSocket Integration</h2>
			<p>
				The Crash game uses WebSocket connections for real-time updates. Connect
				to receive live multiplier updates, bet notifications, and round events.
			</p>

			<h3>Connection</h3>
			<CodeBlock language="typescript" title="WebSocket Connection">
				{`const ws = new WebSocket('wss://api.ringfort.studio/v1/games/crash/ws');

// Authenticate after connection
ws.onopen = () => {
  ws.send(JSON.stringify({
    type: 'authenticate',
    sessionToken: 'your-session-token'
  }));
};

ws.onmessage = (event) => {
  const message = JSON.parse(event.data);
  handleMessage(message);
};

ws.onerror = (error) => {
  console.error('WebSocket error:', error);
};

ws.onclose = () => {
  console.log('WebSocket closed, attempting reconnect...');
  // Implement reconnection logic
};`}
			</CodeBlock>

			<h3>Message Types</h3>
			<p>The server sends the following message types:</p>

			<h4>
				<code>round_start</code> <Badge variant="new">Event</Badge>
			</h4>
			<p>Sent when a new betting phase begins.</p>
			<CodeBlock language="json">
				{`{
  "type": "round_start",
  "roundId": "crash_round_def456",
  "phase": "betting",
  "bettingEndsAt": "2024-01-15T18:31:00.000Z",
  "serverSeedHash": "e5f6a7b8c9d0..."
}`}
			</CodeBlock>

			<h4>
				<code>bet_placed</code> <Badge variant="new">Event</Badge>
			</h4>
			<p>Sent when any player places a bet (including yourself).</p>
			<CodeBlock language="json">
				{`{
  "type": "bet_placed",
  "roundId": "crash_round_def456",
  "playerId": "player_xyz",
  "playerName": "CryptoKing",
  "amount": 50.00,
  "hasAutoCashout": true,
  "timestamp": "2024-01-15T18:30:55.123Z"
}`}
			</CodeBlock>

			<h4>
				<code>round_running</code> <Badge variant="new">Event</Badge>
			</h4>
			<p>Sent when the betting phase ends and the round begins.</p>
			<CodeBlock language="json">
				{`{
  "type": "round_running",
  "roundId": "crash_round_def456",
  "phase": "running",
  "startedAt": "2024-01-15T18:31:00.000Z",
  "totalBets": 12,
  "totalWagered": 450.00
}`}
			</CodeBlock>

			<h4>
				<code>multiplier_update</code> <Badge variant="new">Event</Badge>
			</h4>
			<p>Sent frequently during the running phase with current multiplier.</p>
			<CodeBlock language="json">
				{`{
  "type": "multiplier_update",
  "roundId": "crash_round_def456",
  "multiplier": 1.87,
  "elapsedMs": 10500
}`}
			</CodeBlock>

			<Alert variant="info" title="Update Frequency">
				Multiplier updates are sent approximately every 100ms. Use client-side
				interpolation for smoother animation between updates.
			</Alert>

			<h4>
				<code>player_cashout</code> <Badge variant="new">Event</Badge>
			</h4>
			<p>Sent when any player cashes out.</p>
			<CodeBlock language="json">
				{`{
  "type": "player_cashout",
  "roundId": "crash_round_def456",
  "playerId": "player_xyz",
  "playerName": "CryptoKing",
  "multiplier": 2.45,
  "amount": 50.00,
  "payout": 122.50,
  "isAutoCashout": false,
  "timestamp": "2024-01-15T18:31:15.789Z"
}`}
			</CodeBlock>

			<h4>
				<code>round_crashed</code> <Badge variant="new">Event</Badge>
			</h4>
			<p>Sent when the round crashes and ends.</p>
			<CodeBlock language="json">
				{`{
  "type": "round_crashed",
  "roundId": "crash_round_def456",
  "crashPoint": 2.67,
  "duration": 16450,
  "results": {
    "totalBets": 12,
    "totalWagered": 450.00,
    "totalPayout": 380.50,
    "winners": 8,
    "losers": 4
  },
  "provablyFair": {
    "serverSeed": "revealed_server_seed_here",
    "serverSeedHash": "e5f6a7b8c9d0...",
    "roundSeed": "combined_round_seed"
  }
}`}
			</CodeBlock>

			<h4>
				<code>your_bet_result</code> <Badge variant="new">Event</Badge>
			</h4>
			<p>Personal message with your bet result after round ends.</p>
			<CodeBlock language="json">
				{`{
  "type": "your_bet_result",
  "roundId": "crash_round_def456",
  "betId": "bet_crash_stu456vwx",
  "amount": 15.00,
  "crashPoint": 2.67,
  "cashedOut": true,
  "cashoutMultiplier": 2.45,
  "payout": 36.75,
  "profit": 21.75,
  "balanceAfter": 136.75
}`}
			</CodeBlock>

			<h2>Complete Integration Example</h2>
			<CodeBlock language="typescript" title="crash-integration.ts" showLineNumbers>
				{`interface CrashGameState {
  roundId: string | null;
  phase: 'betting' | 'running' | 'crashed' | 'cooldown';
  multiplier: number;
  myBet: ActiveBet | null;
  activeBets: PublicBet[];
  previousCrashes: number[];
}

interface ActiveBet {
  betId: string;
  amount: number;
  autoCashout: number | null;
  cashedOut: boolean;
  cashoutMultiplier?: number;
}

interface PublicBet {
  playerId: string;
  playerName: string;
  amount: number;
  cashedOut: boolean;
  cashoutMultiplier?: number;
}

class CrashGame {
  private ws: WebSocket | null = null;
  private sessionToken: string;
  private state: CrashGameState;
  private onStateChange: (state: CrashGameState) => void;

  constructor(
    sessionToken: string,
    onStateChange: (state: CrashGameState) => void
  ) {
    this.sessionToken = sessionToken;
    this.onStateChange = onStateChange;
    this.state = {
      roundId: null,
      phase: 'cooldown',
      multiplier: 1.00,
      myBet: null,
      activeBets: [],
      previousCrashes: [],
    };
  }

  connect(): void {
    this.ws = new WebSocket('wss://api.ringfort.studio/v1/games/crash/ws');

    this.ws.onopen = () => {
      console.log('Connected to Crash game');
      this.authenticate();
    };

    this.ws.onmessage = (event) => {
      const message = JSON.parse(event.data);
      this.handleMessage(message);
    };

    this.ws.onclose = () => {
      console.log('Disconnected, reconnecting in 3s...');
      setTimeout(() => this.connect(), 3000);
    };

    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
  }

  private authenticate(): void {
    this.send({
      type: 'authenticate',
      sessionToken: this.sessionToken,
    });
  }

  private send(data: object): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }

  private handleMessage(message: any): void {
    switch (message.type) {
      case 'authenticated':
        console.log('Authentication successful');
        break;

      case 'round_start':
        this.state = {
          ...this.state,
          roundId: message.roundId,
          phase: 'betting',
          multiplier: 1.00,
          myBet: null,
          activeBets: [],
        };
        break;

      case 'bet_placed':
        this.state.activeBets.push({
          playerId: message.playerId,
          playerName: message.playerName,
          amount: message.amount,
          cashedOut: false,
        });
        break;

      case 'round_running':
        this.state.phase = 'running';
        break;

      case 'multiplier_update':
        this.state.multiplier = message.multiplier;
        break;

      case 'player_cashout':
        const bet = this.state.activeBets.find(
          (b) => b.playerId === message.playerId
        );
        if (bet) {
          bet.cashedOut = true;
          bet.cashoutMultiplier = message.multiplier;
        }
        break;

      case 'round_crashed':
        this.state.phase = 'crashed';
        this.state.multiplier = message.crashPoint;
        this.state.previousCrashes.unshift(message.crashPoint);
        if (this.state.previousCrashes.length > 20) {
          this.state.previousCrashes.pop();
        }
        break;

      case 'your_bet_result':
        if (this.state.myBet) {
          this.state.myBet.cashedOut = message.cashedOut;
          if (message.cashedOut) {
            this.state.myBet.cashoutMultiplier = message.cashoutMultiplier;
          }
        }
        break;
    }

    this.onStateChange({ ...this.state });
  }

  async placeBet(amount: number, autoCashout?: number): Promise<void> {
    if (this.state.phase !== 'betting') {
      throw new Error('Can only place bets during betting phase');
    }

    const response = await fetch(
      'https://api.ringfort.studio/v1/games/crash/bet',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: this.sessionToken,
          amount,
          autoCashout,
        }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message);
    }

    const result = await response.json();
    this.state.myBet = {
      betId: result.data.betId,
      amount,
      autoCashout: autoCashout || null,
      cashedOut: false,
    };
    this.onStateChange({ ...this.state });
  }

  async cashout(): Promise<void> {
    if (!this.state.myBet || this.state.myBet.cashedOut) {
      throw new Error('No active bet to cash out');
    }

    if (this.state.phase !== 'running') {
      throw new Error('Can only cash out during running phase');
    }

    const response = await fetch(
      'https://api.ringfort.studio/v1/games/crash/cashout',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken: this.sessionToken,
          betId: this.state.myBet.betId,
        }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message);
    }

    const result = await response.json();
    this.state.myBet.cashedOut = true;
    this.state.myBet.cashoutMultiplier = result.data.cashoutMultiplier;
    this.onStateChange({ ...this.state });
  }

  disconnect(): void {
    this.ws?.close();
    this.ws = null;
  }
}

// Example usage
const game = new CrashGame('your-session-token', (state) => {
  console.log('State updated:', state.phase, state.multiplier);
});

game.connect();

// Later: place a bet with 2x auto-cashout
// game.placeBet(10.00, 2.00);

// Or manually cash out
// game.cashout();`}
			</CodeBlock>

			<h2>Provably Fair Verification</h2>
			<p>
				The crash point for each round is determined before any bets are placed.
				The server commits to the crash point by publishing its hash, which can
				be verified after the round.
			</p>

			<Step number={1} title="Server Commits to Crash Point">
				<p>
					Before each round, you receive a <code>serverSeedHash</code>. This is
					the SHA-256 hash of the combined server seed and round number.
				</p>
			</Step>

			<Step number={2} title="Round Plays Out">
				<p>
					Players place bets and the multiplier grows. The crash point is
					already determined but unknown to players.
				</p>
			</Step>

			<Step number={3} title="Crash Point Revealed">
				<p>
					When the round crashes, the server reveals the seed. You can verify
					the crash point was predetermined.
				</p>
			</Step>

			<Step number={4} title="Verify the Result">
				<p>
					Use the revealed seed to recalculate the crash point and verify it
					matches.
				</p>
			</Step>

			<CodeBlock language="typescript" title="Crash Point Generation & Verification">
				{`import { createHash, createHmac } from 'crypto';

const HOUSE_EDGE = 0.01; // 1%

/**
 * Generate crash point from a seed
 * This is the exact algorithm used by the server
 */
function generateCrashPoint(seed: string): number {
  // Use HMAC-SHA256 with the seed
  const hash = createHmac('sha256', seed)
    .update('crash')
    .digest('hex');

  // Take first 13 hex characters (52 bits)
  const h = parseInt(hash.substring(0, 13), 16);

  // Map to crash point with house edge
  // Formula ensures 1% of rounds crash at 1.00x (instant crash)
  const e = Math.pow(2, 52);

  // If divisible by 101 (approximately 1%), instant crash
  if (h % 101 === 0) {
    return 1.00;
  }

  // Calculate crash point
  const crashPoint = (100 * e - h) / (e - h);

  // Apply house edge and round down to 2 decimals
  const withHouseEdge = crashPoint * (1 - HOUSE_EDGE);
  return Math.floor(withHouseEdge * 100) / 100;
}

/**
 * Verify a crash round result
 */
function verifyCrashResult(
  serverSeed: string,
  serverSeedHash: string,
  roundNumber: number,
  expectedCrashPoint: number
): boolean {
  // Step 1: Create the round seed
  const roundSeed = \`\${serverSeed}:\${roundNumber}\`;

  // Step 2: Verify the server seed hash
  const computedHash = createHash('sha256')
    .update(roundSeed)
    .digest('hex');

  if (computedHash !== serverSeedHash) {
    console.error('Server seed hash mismatch!');
    return false;
  }

  // Step 3: Calculate the crash point
  const calculatedCrashPoint = generateCrashPoint(roundSeed);

  // Step 4: Compare (allow small floating point tolerance)
  if (Math.abs(calculatedCrashPoint - expectedCrashPoint) > 0.01) {
    console.error(
      \`Crash point mismatch! Expected \${expectedCrashPoint}, got \${calculatedCrashPoint}\`
    );
    return false;
  }

  console.log(\`Verified! Crash point: \${calculatedCrashPoint}x\`);
  return true;
}

// Example verification
verifyCrashResult(
  'server_seed_abc123',
  'hash_of_server_seed_abc123_round_42',
  42,
  2.67
);`}
			</CodeBlock>

			<Alert variant="success" title="Chain Verification">
				Each round's server seed is derived from the previous round, creating a
				verifiable chain. You can verify the entire history of crash points back
				to the genesis seed.
			</Alert>

			<h2>Strategy Considerations</h2>
			<p>
				While Crash is a game of chance, understanding the mathematics can help
				inform your strategy:
			</p>

			<h3>Crash Point Distribution</h3>
			<ul>
				<li>
					<strong>~1% of rounds</strong> crash instantly at 1.00x
				</li>
				<li>
					<strong>~50% of rounds</strong> crash before 2.00x
				</li>
				<li>
					<strong>~33% of rounds</strong> reach 3.00x or higher
				</li>
				<li>
					<strong>~10% of rounds</strong> reach 10.00x or higher
				</li>
				<li>
					<strong>~1% of rounds</strong> reach 100.00x or higher
				</li>
			</ul>

			<h3>Auto-Cashout vs Manual</h3>
			<ul>
				<li>
					<strong>Auto-cashout:</strong> Guaranteed to execute at your target
					(if reached), no network latency risk
				</li>
				<li>
					<strong>Manual cashout:</strong> Flexible, can react to gut feeling,
					but network delay may cause missed targets
				</li>
			</ul>

			<h3>Bankroll Management</h3>
			<ul>
				<li>Set a loss limit per session before playing</li>
				<li>Consider lower auto-cashout targets (1.5x-2x) for more consistent wins</li>
				<li>Higher targets have bigger potential rewards but crash before reaching them more often</li>
				<li>The expected value is always -1% of your total wagers over time</li>
			</ul>

			<h2>Error Handling</h2>
			<Table
				columns={[
					{ key: 'code', header: 'Error Code' },
					{ key: 'message', header: 'Message' },
					{ key: 'solution', header: 'Solution' },
				]}
				data={[
					{
						code: 'BETTING_CLOSED',
						message: 'Betting phase has ended',
						solution: 'Wait for the next round to place a bet',
					},
					{
						code: 'ALREADY_BET',
						message: 'Already have an active bet this round',
						solution: 'Only one bet per round is allowed',
					},
					{
						code: 'ROUND_NOT_RUNNING',
						message: 'Cannot cash out - round is not running',
						solution: 'Cashout only works during the running phase',
					},
					{
						code: 'ALREADY_CASHED_OUT',
						message: 'Already cashed out this round',
						solution: 'You can only cash out once per round',
					},
					{
						code: 'NO_ACTIVE_BET',
						message: 'No active bet to cash out',
						solution: 'Place a bet first before attempting cashout',
					},
					{
						code: 'INVALID_AUTO_CASHOUT',
						message: 'Auto-cashout must be at least 1.01x',
						solution: 'Set a higher auto-cashout target',
					},
					{
						code: 'INSUFFICIENT_BALANCE',
						message: 'Insufficient balance for bet',
						solution: 'Reduce bet amount or add funds',
					},
				]}
			/>

			<div className={styles.nextSteps}>
				<h2>Next Steps</h2>
				<ul>
					<li>
						<a href="/docs/provably-fair">
							Learn more about Provably Fair verification
						</a>
					</li>
					<li>
						<a href="/docs/webhooks">Set up webhooks for crash round notifications</a>
					</li>
					<li>
						<a href="/docs/games-dice">Try the single-player Dice game</a>
					</li>
				</ul>
			</div>
		</div>
	);
}
